import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import BodyContainer from "../components/BodyContainer";
import BottomContainer from "../components/BottomContainer";
import Footer from "../components/Footer";
import { Helmet } from "react-helmet-async";
import "../TestPage.css";
import WatermarkOverlay from "../components/WatermarkOverlay";
import "../Watermark.css";

import { InlineMath, BlockMath } from "react-katex"; // ✅ Correct imports
import "katex/dist/katex.min.css";
import LegalWarningModal from "../components/LegalWarningModal"; // ✅ Ensure default import


function renderTextWithLatex(text) {
  if (!text) return null;

  const parts = text.split(/(\$[^$]+\$)/g);
  return parts.map((part, idx) => {
    if (part.startsWith("$") && part.endsWith("$")) {
      return <InlineMath key={idx} math={part.slice(1, -1)} />;
    }
    return <span key={idx}>{part}</span>;
  });
}

function TestPage() {
  const { testId } = useParams();
  const navigate = useNavigate();
  const [test, setTest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [score, setScore] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(false);
  const [showIntro, setShowIntro] = useState(true);
  const [showWarning, setShowWarning] = useState(false);

  const API_URL = process.env.REACT_APP_API_BASE_URL;
  const user = JSON.parse(localStorage.getItem("user"));

  // 🔒 Security: trigger warning if screenshot attempt detected
  const triggerWarning = async (reason) => {
    setShowWarning(true);

    try {
      await fetch(`${API_URL}/api/security/log`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId: user?.Id,
          reason,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
        }),
      });
    } catch (err) {
      console.error("Security log failed", err);
    }
  };

  // 🔒 Detect screenshot keys
useEffect(() => {
  const handleKeyDown = (e) => {
    const key = e.key.toLowerCase();

    // Screenshot detection: includes common variations of PrintScreen
    const isScreenshot =
      ["printscreen", "prtsc", "prtscr", "prtscn"].includes(key) || // PrintScreen / PrtSc variations
      ((e.ctrlKey || e.metaKey) && key === "p") ||                // Ctrl+P or Cmd+P
      ((e.ctrlKey || e.metaKey) && e.shiftKey && key === "s") || // Ctrl+Shift+S or Cmd+Shift+S
      (e.metaKey && e.shiftKey && ["3", "4"].includes(key));      // Mac Cmd+Shift+3/4

    // Copy/Save detection
    const isCopyOrSave =
      (e.ctrlKey || e.metaKey) && ["c", "s"].includes(key);       // Ctrl/Cmd+C or Ctrl/Cmd+S

    if (isScreenshot) {
      e.preventDefault();
      triggerWarning("screenshot_attempt");
    } else if (isCopyOrSave) {
      e.preventDefault();
      triggerWarning(`copy_or_save_${key}`);
    }
  };

  

  window.addEventListener("keydown", handleKeyDown);

  return () => {
    window.removeEventListener("keydown", handleKeyDown);
  };
}, []);



  // Fetch test data
  useEffect(() => {
    async function fetchTest() {
      try {
        const response = await fetch(`${API_URL}/api/test/${testId}`);
        if (!response.ok) throw new Error("Failed to fetch test data");

        const data = await response.json();
        const shuffledQuestions = [...data.Questions].sort(() => Math.random() - 0.5);
        setTest({ ...data, Questions: shuffledQuestions });

        if (data.TimeLimit) setTimeLeft(data.TimeLimit * 60);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    fetchTest();
  }, [testId]);

  // Timer countdown
  useEffect(() => {
    if (timeLeft === null || submitted) return;
    const timer = setInterval(() => setTimeLeft((prev) => prev - 1), 1000);
    return () => clearInterval(timer);
  }, [timeLeft, submitted]);

  const formatTime = () => {
    const absTime = Math.abs(timeLeft);
    const minutes = Math.floor(absTime / 60);
    const seconds = absTime % 60;
    const color = timeLeft < 0 ? "red" : "green";
    return (
      <span style={{ color, fontWeight: "bold" }}>
        {timeLeft < 0 ? "-" : ""}
        {minutes}:{seconds < 10 ? `0${seconds}` : seconds}
      </span>
    );
  };

  const handleAnswerChange = (value, type) => {
    setAnswers((prev) => {
      if (type === "Select All") {
        const prevAnswers = prev[currentQuestionIndex] || [];
        return {
          ...prev,
          [currentQuestionIndex]: prevAnswers.includes(value)
            ? prevAnswers.filter((ans) => ans !== value)
            : [...prevAnswers, value],
        };
      }
      return { ...prev, [currentQuestionIndex]: value };
    });

    if (type === "Select All") {
      setSelectedAnswer(
        answers[currentQuestionIndex] && answers[currentQuestionIndex].length > 0
      );
    } else if (type === "Fill In") {
      setSelectedAnswer(value.trim() !== "");
    } else {
      setSelectedAnswer(!!value);
    }
  };

  const handleNextQuestion = () => {
    setSelectedAnswer(false);
    setCurrentQuestionIndex((prevIndex) => prevIndex + 1);
  };

  const handleSubmitTest = async () => {
    let totalCorrect = 0;

    test.Questions.forEach((question, index) => {
      const userAnswer = answers[index] || "";
      let isCorrect = false;

      if (question.Type === "Select All") {
        const correctAnswers = question.CorrectAnswer.map(ans => ans.trim()).sort().join(",");
        const userAnswers = (Array.isArray(userAnswer) ? userAnswer.map(ans => ans.trim()) : []).sort().join(",");
        isCorrect = correctAnswers === userAnswers;
      } else if (question.Type === "Fill In") {
        const correctAnswer = Array.isArray(question.CorrectAnswer)
          ? question.CorrectAnswer.map(ans => ans.toLowerCase().trim())
          : [question.CorrectAnswer.toLowerCase().trim()];
        isCorrect = correctAnswer.includes(userAnswer.toLowerCase().trim());
      } else {
        isCorrect = userAnswer === question.CorrectAnswer;
      }

      if (isCorrect) totalCorrect++;
    });

    setScore(totalCorrect);
    setSubmitted(true);

    if (!user?.Id) return;

    try {
      await fetch(`${API_URL}/api/testscore/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId: user.Id,
          testId: test.Id,
          score: totalCorrect,
        }),
      });
    } catch (error) {
      console.error("Error saving test score:", error);
    }
  };

  return (
    <>
      <Helmet>
        <title>PharmPrepPro - {test?.Name || "Test"}</title>
      </Helmet>

      <BottomContainer>
        <BodyContainer>
          {loading ? (
            <p className="test-loading">Loading test...</p>
          ) : error ? (
            <p className="test-error">{error}</p>
          ) : !test ? (
            <p className="test-error">Test not found.</p>
          ) : showIntro ? (
            <div className="test-intro-container">
              <h1 className="test-title">{test.Name}</h1>
              <p className="test-description">{test.Description}</p>
              <button className="start-test-button" onClick={() => setShowIntro(false)}>Start Exam</button>
            </div>
          ) : (
            <div className="test-container">
              <h1 className="test-title">{test.Name}</h1>
              <p className="test-timer">⏳ Time Remaining: {formatTime()}</p>

              {!submitted ? (
                <div className="question-card">

                  {(test.Questions[currentQuestionIndex].PreSignedImageURL || test.Questions[currentQuestionIndex].Image) && (
                    <div className="question-image">
                      <img
                        src={test.Questions[currentQuestionIndex].PreSignedImageURL || `${process.env.REACT_APP_S3_BASE_URL}/${test.Questions[currentQuestionIndex].Image}`}
                        alt="Question related"
                        style={{ maxWidth: "100%", height: "auto", margin: "10px 0" }}
                      />
                    </div>
                  )}

                  <p className="question-text">
                    {currentQuestionIndex + 1}. {renderTextWithLatex(test.Questions[currentQuestionIndex].Question)}
                  </p>

                  {["Multiple Choice", "Select All"].includes(test.Questions[currentQuestionIndex].Type) ? (
                    <div className="question-options">
                      {Object.entries(test.Questions[currentQuestionIndex].Options).map(([key, value]) => (
                        <label key={key} className="option-label">
                          <input
                            type={test.Questions[currentQuestionIndex].Type === "Multiple Choice" ? "radio" : "checkbox"}
                            name={`question-${currentQuestionIndex}`}
                            value={key}
                            checked={Array.isArray(answers[currentQuestionIndex])
                              ? answers[currentQuestionIndex].includes(key)
                              : answers[currentQuestionIndex] === key}
                            onChange={(e) => handleAnswerChange(e.target.value, test.Questions[currentQuestionIndex].Type)}
                          />
                          {renderTextWithLatex(value)}
                        </label>
                      ))}
                    </div>
                  ) : (
                    <input
                      type="text"
                      className="fill-blank-input"
                      placeholder="Your answer..."
                      onChange={(e) => handleAnswerChange(e.target.value, "Fill In")}
                    />
                  )}

                  <div className="navigation-buttons">
                    {currentQuestionIndex < test.Questions.length - 1 ? (
                      <button
                        className={`next-button ${!selectedAnswer ? "disabled" : ""}`}
                        onClick={handleNextQuestion}
                        disabled={!selectedAnswer}
                      >
                        Next Question
                      </button>
                    ) : (
                      <button className="submit-button" onClick={handleSubmitTest}>Submit Test</button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="test-results">
                  <WatermarkOverlay user={user} />
                  <h2>Results</h2>
                  <p className="score-text">
                    Your Score: {score} / {test.Questions.length}
                  </p>
                  <h3>Review Answers</h3>
                  <ul className="review-list">
                    {test.Questions.map((question, index) => {
                      const userAnswer = answers[index];
                      const correctAnswer = question.CorrectAnswer;

                      const getAnswerText = (answer, type) => {
                        if (type === "Multiple Choice" || type === "Select All") {
                          if (Array.isArray(answer)) {
                            return answer
                              .map((ans) => {
                                const option = question.Options?.[ans];
                                return option ? `${ans}: ${option}` : null;
                              })
                              .filter(Boolean)
                              .join(", ");
                          } else {
                            return question.Options?.[answer]
                              ? `${answer}: ${question.Options[answer]}`
                              : "No Answer";
                          }
                        } else {
                          return answer || "No Answer";
                        }
                      };

                      const imageUrl =
                        question.PreSignedImageURL ||
                        (question.Image
                          ? `${process.env.REACT_APP_S3_BASE_URL}/${question.Image}`
                          : null);

                      return (
                        <li
                          key={index}
                          className={`review-item ${
                            userAnswer?.toString() === correctAnswer?.toString()
                              ? "correct"
                              : "incorrect"
                          }`}
                        >
                          <p>
                            <strong>Q{index + 1}:</strong>{" "}
                            {renderTextWithLatex(question.Question)}
                          </p>

                          {imageUrl && (
                            <div className="question-image">
                              <img
                                src={imageUrl}
                                alt={`Question ${index + 1}`}
                                style={{
                                  maxWidth: "100%",
                                  height: "auto",
                                  margin: "10px 0",
                                  borderRadius: "8px",
                                }}
                              />
                            </div>
                          )}

                          <p>
                            <strong>Your Answer:</strong>{" "}
                            {renderTextWithLatex(getAnswerText(userAnswer, question.Type))}
                          </p>
                          <p>
                            <strong>Correct Answer:</strong>{" "}
                            {renderTextWithLatex(getAnswerText(correctAnswer, question.Type))}
                          </p>
                          <p className="explanation">
                            <strong>Explanation:</strong>{" "}
                            {renderTextWithLatex(question.Explanation)}
                          </p>
                        </li>
                      );
                    })}
                  </ul>
                  <button
                    className="back-button"
                    onClick={() => navigate("/practice-test")}
                  >
                    Back to Practice Tests
                  </button>
                </div>
              )}
            </div>
          )}
        </BodyContainer>
        <Footer />
        
      </BottomContainer>
      {/* ✅ Security Modal */}
        <LegalWarningModal
          show={showWarning}
          onClose={() => setShowWarning(false)}
        />
    </>
  );
}

export default TestPage;
